<template>
  <v-menu v-if="featureIsActive('s_type_crawling_pests')" offset-y>
    <template v-slot:activator="{ on, attrs }">
      <round-button
        icon="file_download"
        data-testid="export-csv-button"
        v-bind="attrs"
        v-on="on"
      >
        {{ $t('label') }}
        <span class="ml-1">&#9660;</span>
      </round-button>
    </template>
    <v-list>
      <v-list-item
        link
        data-testid="export-csv-format-flying_only"
        @click="exportCsv(LOCOMOTION_SCOPE.FLYING_ONLY)"
      >
        {{ $t('format.flying_only') }}
      </v-list-item>
      <v-list-item
        link
        data-testid="export-csv-format-crawling_aware"
        @click="exportCsv(LOCOMOTION_SCOPE.ALL)"
      >
        {{ $t('format.crawling_aware') }}
      </v-list-item>
    </v-list>
  </v-menu>
  <round-button
    v-else
    icon="file_download"
    data-testid="export-csv-button"
    @click="exportCsv(LOCOMOTION_SCOPE.FLYING_ONLY)"
  >
    {{ $t('label') }}
  </round-button>
</template>

<i18n lang="yaml">
ja:
  label: 'CSV出力'
  format:
    flying_only: '従来フォーマット（ライトトラップのみ）'
    crawling_aware: '床置きトラップ対応版'
  error:
    selectData: 'CSV出力するデータを選択して下さい'
en:
  label: 'CSV Download'
  format:
    flying_only: 'Flying-only format'
    crawling_aware: 'Crawling-aware version'
  error:
    selectData: 'Select the data for which you want to download as CSV.'
</i18n>

<script>
import { endOfDay, startOfDay } from 'date-fns';
import { arrayToCsvRow, exportCsv } from '@/libs/csv';
import dateTimeFormatting from '@/mixins/dateTimeFormatting';
import Features from '@/mixins/features';
import {
  LOCOMOTION_SCOPE,
  locomotionOf,
  flyingInsectTypeMap,
  allInsectTypeMap,
  normalizeFlyingCounts,
  normalizeAllCounts,
} from '@/libs/insectTypes';
import LocalizedTerms from '@/mixins/localizedTerms';
import Notifications from '@/mixins/notifications';
import RoundButton from '@/components/atoms/RoundButton';

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const calcDays = (event) => {
  const beginning = startOfDay(new Date(event.installed_at));
  const end = endOfDay(new Date(event.collected_at));
  return Math.ceil((end.getTime() - beginning.getTime()) / MILLISECONDS_PER_DAY);
};

export default {
  name: 'export-csv-button',
  props: {
    events: { type: Array, required: true },
    place: { type: String, required: true },
  },
  components: { RoundButton },
  mixins: [Features, LocalizedTerms, Notifications, dateTimeFormatting],
  computed: {
    LOCOMOTION_SCOPE: () => LOCOMOTION_SCOPE,
  },
  methods: {
    stationTerm(key) {
      return this.term(`station.${key}`);
    },
    buildBeforeInsectCountColumns(scope) {
      const columns = [
        { header: 'No', value: (_, index) => index + 1 },
        { header: this.term('sensor.place'), value: () => this.place },
        { header: this.stationTerm('trap_no'), value: event => event.trap_no },
      ];
      if (scope === LOCOMOTION_SCOPE.ALL) {
        columns.push({
          header: this.stationTerm('locomotion'),
          value: event => this.stationTerm(`locomotion_label.${locomotionOf(event)}`),
        });
      }
      columns.push(
        { header: this.stationTerm('installed_at'), value: event => this.formatDate(event.installed_at) },
        { header: this.stationTerm('collected_at'), value: event => this.formatDate(event.collected_at) },
        { header: this.stationTerm('days'), value: event => calcDays(event) },
        { header: this.stationTerm('month'), value: event => event.month },
        { header: this.stationTerm('upload_time'), value: event => this.formatDateTimeWithSeconds(event.upload_time) },
        { header: this.stationTerm('photo_count'), value: event => event.photoCount },
      );
      return columns;
    },
    // others 列は末尾に並べる（key === 'others' で判別、i18n ラベル非依存）。
    buildInsectCountColumns(scope) {
      const insectTypeMap = scope === LOCOMOTION_SCOPE.ALL
        ? allInsectTypeMap(this.$i18n.locale)
        : flyingInsectTypeMap(this.$i18n.locale);
      const normalizeCounts = scope === LOCOMOTION_SCOPE.ALL
        ? normalizeAllCounts
        : normalizeFlyingCounts;
      const entries = this._.map(insectTypeMap, (type, key) => ({
        key,
        header: type.label,
        value: event => normalizeCounts(event.insect_counts)[key] || 0,
      }));
      const withoutOthers = entries.filter(({ key }) => key !== 'others');
      const others = entries.find(({ key }) => key === 'others');
      return others ? [...withoutOthers, others] : withoutOthers;
    },
    buildAfterInsectCountColumns() {
      return [
        { header: this.stationTerm('insect_count'), value: event => event.insectCount },
        { header: this.stationTerm('index'), value: event => (event.insectCount / calcDays(event)).toFixed(2) },
      ];
    },
    columns(scope) {
      return this._.concat(
        this.buildBeforeInsectCountColumns(scope),
        this.buildInsectCountColumns(scope),
        this.buildAfterInsectCountColumns(),
      );
    },
    exportCsv(scope) {
      if (this.events.length === 0) {
        this.notifyError('error.selectData');
        return;
      }
      const cols = this.columns(scope);
      const headerRow = this._.map(cols, 'header');
      const dataRows = this._.map(this.events, (event, index) =>
        cols.map(c => c.value(event, index)));
      const rows = this._.concat([headerRow], dataRows);
      const csvString = rows.map(arrayToCsvRow).join('\n');
      exportCsv(csvString, `${this.place}.csv`);
    },
  },
};
</script>
